import React, { useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { DatePicker, Drawer, Dropdown, Menu } from 'antd'
import { Link, useHistory } from 'react-router-dom'
import { AiOutlineDown } from 'react-icons/ai'
import Select from 'react-select'

import Receive from '../../components/reusable/receive'
import ClaimStatusCard from '../../components/reusable/claimStatusCard'
import { stringifyDate, useListData, usePet, useQuery } from '../../modules'

const { RangePicker } = DatePicker

const actionedOptions = [
    { label: 'Open only', value: 'open' },
    { label: 'Actioned only', value: 'actioned' },
    { label: 'All', value: 'all' },
]

const inferSuggestedActions = reason => {
    const text = String(reason ?? '').toLowerCase()
    if (!text.trim()) return ['Review the claim history and contact SHA support for clarification.']

    const actions = []

    if (text.includes('pre-auth') || text.includes('preauth') || text.includes('authorization'))
        actions.push('Obtain or confirm the SHA-finalised preauthorization, then re-attach it to the affected billed item.')

    if (text.includes('missing') || text.includes('attachment') || text.includes('document'))
        actions.push('Upload or tag the missing supporting documents before the next resubmission attempt.')

    if (text.includes('icd') || text.includes('diagnos'))
        actions.push('Review the diagnosis coding and make sure the claim is using the correct ICD-11 code set.')

    if (text.includes('intervention') || text.includes('code') || text.includes('regulatory'))
        actions.push('Check the billed sale items for the required intervention or regulatory codes and correct any missing mappings.')

    if (text.includes('late') || text.includes('timeline') || text.includes('expired'))
        actions.push('Confirm the filing timeline and resubmit only after the visit dates and claim timestamps are correct.')

    if (actions.length === 0)
        actions.push('Review the claim payload in the claim status card and contact SHA support if the payer reason remains unclear.')

    actions.push('Return to the claim work surface, correct the blocker, and resubmit the claim.')

    return Array.from(new Set(actions))
}

const RejectedClaims = () => {
    const query = useQuery()
    const history = useHistory()
    const initialClaim = query.get('claim') || undefined
    const defaultRanges = [moment().subtract(30, 'days').startOf('day'), moment().endOf('day')]
    const [string, setString] = useState('')
    const [selected, setSelected] = useState()
    const [detailsVisible, setDetailsVisible] = useState(Boolean(initialClaim))
    const [pendingAction, setPendingAction] = useState(false)
    const [actionNote, setActionNote] = useState('Reviewed from rejected claims worklist')
    const [actionedFilter, setActionedFilter] = useState(actionedOptions[0])
    const [ranges, setRanges] = useState(initialClaim ? null : defaultRanges)
    const [claimFilter, setClaimFilter] = useState(initialClaim)
    const [selectedInsurer, setSelectedInsurer] = useState({ label: 'All insurers', value: 'all' })
    const [selectedScheme, setSelectedScheme] = useState({ label: 'All schemes', value: 'all' })

    const actionedValue = useMemo(() => {
        if (actionedFilter?.value === 'open') return false
        if (actionedFilter?.value === 'actioned') return true
        return undefined
    }, [actionedFilter])

    const [
        { loading, error, fetch },
        { list: claims = [], count = 0 },
        { navigate, Paginate, page = 1, limit = 10 }
    ] = useListData('rejectedClaims', {
        string,
        claim: claimFilter,
        actioned: actionedValue,
        insurer: selectedInsurer?.value !== 'all' ? selectedInsurer?.value : undefined,
        scheme: selectedScheme?.value !== 'all' ? selectedScheme?.value : undefined,
        from: ranges?.[0]?._d ?? ranges?.[0],
        to: ranges?.[1]?._d ?? ranges?.[1],
    }, { notify: true, manual: true }, 'claims')

    const [{ data: insurersData }] = usePet('insuranceCompanies', { limit: 100 }, { notify: false })
    const [{ data: schemesData }] = usePet('schemes', { active: true, limit: 200 }, { notify: false })

    const insurerOptions = [
        { label: 'All insurers', value: 'all' },
        ...((insurersData?.insurances ?? []).filter(insurer => insurer?.isSha).map(insurer => ({
            label: insurer.name,
            value: insurer._id,
        })))
    ]

    const schemeOptions = [
        { label: 'All schemes', value: 'all' },
        ...((schemesData?.schemes ?? [])
            .filter(scheme => scheme?.operatedBy?.isSha)
            .filter(scheme => selectedInsurer?.value === 'all' || String(scheme?.operatedBy?._id) === String(selectedInsurer?.value))
            .map(scheme => ({
                label: scheme.name,
                value: scheme._id,
            })))
    ]

    const onComplete = (_, notif, ops) => {
        notif('Claim marked as actioned', ops('success'))
        fetch()
        if (selected?._id) {
            setSelected(prev => prev ? { ...prev, actioned: true, actionedAt: new Date().toISOString() } : prev)
        }
    }

    const [, markActioned] = usePet('markClaimActioned', {
        id: selected?._id,
        note: actionNote?.trim() || 'Reviewed from rejected claims worklist',
    }, { notify: true, callback: onComplete, manual: true, load: { msg: 'Updating claim...' } })

    useEffect(() => {
        fetch()
    }, [page]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        navigate(1)
        fetch()
    }, [string, actionedValue, claimFilter, selectedInsurer?.value, selectedScheme?.value, ranges?.[0]?.valueOf?.() ?? ranges?.[0], ranges?.[1]?.valueOf?.() ?? ranges?.[1]]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (selectedScheme?.value === 'all') return

        const match = schemeOptions.find(option => option.value === selectedScheme?.value)
        if (!match) setSelectedScheme({ label: 'All schemes', value: 'all' })
    }, [selectedInsurer?.value, schemesData]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!pendingAction || !selected?._id) return

        setPendingAction(false)
        markActioned()
    }, [pendingAction, selected?._id]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!claimFilter || claims.length === 0) return

        const matched = claims.find(claim => claim?._id === claimFilter)
        if (matched) {
            setSelected(matched)
            setDetailsVisible(true)
        }
    }, [claimFilter, claims])

    const openDetails = claim => {
        setSelected(claim)
        setDetailsVisible(true)
        setClaimFilter(claim?._id)
        history.replace(`/app/accounts/sales/rejected-claims?claim=${claim?._id}`)
    }

    const closeDetails = () => {
        setDetailsVisible(false)
        setSelected()
        setClaimFilter(undefined)
        history.replace('/app/accounts/sales/rejected-claims')
    }

    const clearFilters = () => {
        setString('')
        setActionedFilter(actionedOptions[0])
        setRanges(defaultRanges)
        setSelectedInsurer({ label: 'All insurers', value: 'all' })
        setSelectedScheme({ label: 'All schemes', value: 'all' })
        if (claimFilter) {
            setClaimFilter(undefined)
            history.replace('/app/accounts/sales/rejected-claims')
        }
    }

    const markSelectedActioned = claim => {
        setSelected(claim)
        setPendingAction(true)
    }

    const suggestedActions = inferSuggestedActions(selected?.rejectionReason)

    return (
        <div className="px-4 sm:px-6 lg:px-8 py-8 w-full mx-auto">
            {
                detailsVisible &&
                <Drawer
                    visible={ detailsVisible }
                    width="42%"
                    onClose={ closeDetails }
                    closable
                    maskClosable
                    title={ selected?.patientData?.name ?? 'Claim details' }>
                    {
                        selected &&
                        <>
                            <div className="mb-4 rounded-sm border border-gray-200 bg-gray-50 px-4 py-3">
                                <div className="grid gap-3 md:grid-cols-2 sm:grid-cols-1">
                                    <div>
                                        <p className="text-xs text-gray-500 mb-1">Claim ID</p>
                                        <p className="text-sm font-medium mb-0">{selected?._id}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500 mb-1">Actioned</p>
                                        <p className="text-sm font-medium mb-0">{selected?.actioned ? `Yes${selected?.actionedAt ? ` • ${stringifyDate(selected.actionedAt)}` : ''}` : 'No'}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500 mb-1">Insurer</p>
                                        <p className="text-sm font-medium mb-0">{selected?.insurerData?.name ?? '—'}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500 mb-1">Rejected On</p>
                                        <p className="text-sm font-medium mb-0">{stringifyDate(selected?.rejectionTimestamp ?? selected?.updatedOn)}</p>
                                    </div>
                                </div>
                                {
                                    !selected?.actioned &&
                                    <div className="mt-4">
                                        <label className="block text-xs text-gray-500 mb-1">Follow-up note</label>
                                        <textarea
                                            rows="3"
                                            value={ actionNote }
                                            onChange={ e => setActionNote(e.target.value) }
                                            className="appearance-none block w-full bg-white text-gray-700 border border-gray-200 rounded py-2 px-3 leading-tight focus:outline-none focus:bg-white"
                                            placeholder="Add a short note about what was done for this rejection" />
                                        <div className="mt-3 text-right">
                                            <button
                                                onClick={ () => markSelectedActioned(selected) }
                                                className="btn bg-green-600 hover:bg-green-700 text-white">
                                                Mark Actioned
                                            </button>
                                        </div>
                                    </div>
                                }
                                <div className="mt-4 rounded-sm border border-yellow-200 bg-yellow-50 px-4 py-3">
                                    <p className="text-xs font-semibold text-yellow-800 mb-2">Suggested next steps</p>
                                    <div className="space-y-1">
                                        {
                                            suggestedActions.map((step, index) =>
                                                <p key={ `${index}-${step}` } className="text-sm text-yellow-900 mb-0">
                                                    {index + 1}. {step}
                                                </p>
                                            )
                                        }
                                    </div>
                                </div>
                            </div>
                            <ClaimStatusCard claimId={ selected?._id } admissionId={ selected?.admission } orderId={ selected?.order } />
                        </>
                    }
                </Drawer>
            }
            <div className="sm:flex sm:justify-between sm:items-start mb-6 gap-4">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-800 mb-1">Rejected Claims</h1>
                    <p className="text-sm text-gray-500 mb-0">Review, filter, and follow up on SHA claims rejected by the payer.</p>
                </div>
                <div className="text-sm text-gray-500">
                    {count} claim{count === 1 ? '' : 's'} found
                </div>
            </div>
            <div className="bg-white shadow-lg rounded-sm border border-gray-200 p-4 mb-4">
                <div className="grid gap-3 md:grid-cols-6 sm:grid-cols-1">
                    <input
                        value={ string }
                        onChange={ e => setString(e.target.value) }
                        className="appearance-none block w-full bg-gray-100 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white"
                        id="grid-search"
                        type="text"
                        placeholder="Search by patient, reason, claim ref" />
                    <Select
                        value={ selectedInsurer }
                        onChange={ setSelectedInsurer }
                        options={ insurerOptions }
                        placeholder="Select insurer" />
                    <Select
                        value={ selectedScheme }
                        onChange={ setSelectedScheme }
                        options={ schemeOptions }
                        placeholder="Select scheme" />
                    <Select
                        value={ actionedFilter }
                        onChange={ option => setActionedFilter(option ?? actionedOptions[0]) }
                        options={ actionedOptions }
                        placeholder="Select status" />
                    <RangePicker
                        className="w-full"
                        value={ ranges }
                        onChange={ next => setRanges(next && next?.length === 2 ? next : null) } />
                    <div className="flex items-center gap-2">
                        <button onClick={ clearFilters } className="btn bg-gray-100 hover:bg-gray-200 text-gray-700 w-full">Clear Filters</button>
                    </div>
                </div>
                {
                    claimFilter &&
                    <p className="text-xs text-blue-700 mt-3 mb-0">Focused on claim {claimFilter}. Clear filters to return to the full worklist.</p>
                }
            </div>
            <div className="col-span-full xl:col-span-8 bg-white shadow-lg rounded-sm border border-gray-200">
                <header className="px-5 py-4 border-b border-gray-100">
                    <h2 className="font-semibold text-gray-800">Rejected Claims Worklist</h2>
                </header>
                <div className="p-3">
                    <div className="overflow-x-auto">
                        <Receive
                            progress
                            loading={loading}
                            error={error}>
                            {
                                claims.length > 0 ?
                                    <table className="table-auto w-full">
                                        <thead className="text-xs uppercase text-gray-400 bg-gray-50 rounded-sm">
                                            <tr>
                                                <th className="p-2"><div className="font-semibold text-center">No</div></th>
                                                <th className="p-2"><div className="font-semibold text-left">Patient</div></th>
                                                <th className="p-2"><div className="font-semibold text-left">Insurer</div></th>
                                                <th className="p-2"><div className="font-semibold text-left">Reason</div></th>
                                                <th className="p-2"><div className="font-semibold text-center">Rejected On</div></th>
                                                <th className="p-2"><div className="font-semibold text-center">Actioned</div></th>
                                                <th className="p-2"><div className="font-semibold text-center"></div></th>
                                            </tr>
                                        </thead>
                                        <tbody className="text-sm font-medium divide-y divide-gray-100">
                                            {
                                                claims.map((claim, key) =>
                                                    <tr key={claim._id} className={ claimFilter === claim._id ? 'bg-blue-50' : '' }>
                                                        <td className="p-2">
                                                            <div className="text-center">{((page - 1) * limit) + key + 1}.</div>
                                                        </td>
                                                        <td className="p-2">
                                                            <div>{claim?.patientData?.name ?? 'Unknown patient'}</div>
                                                            <p className="text-gray-500 my-0 text-xs italic">
                                                                {claim?.serviceType ?? '—'}
                                                                {claim?.authorizationCode ? ` • ${claim.authorizationCode}` : ''}
                                                                {claim?.admissionData?.indicator ? ` • ${claim.admissionData.indicator}` : ''}
                                                            </p>
                                                        </td>
                                                        <td className="p-2">
                                                            <div>{claim?.insurerData?.name ?? '—'}</div>
                                                        </td>
                                                        <td className="p-2">
                                                            <div className="max-w-lg">{claim?.rejectionReason ?? 'No rejection reason recorded'}</div>
                                                            <p className="text-gray-500 my-0 text-xs italic">
                                                                {(inferSuggestedActions(claim?.rejectionReason)?.[0])}
                                                            </p>
                                                        </td>
                                                        <td className="p-2">
                                                            <div className="text-center">{stringifyDate(claim?.rejectionTimestamp ?? claim?.updatedOn)}</div>
                                                        </td>
                                                        <td className="p-2">
                                                            <div className="text-center">
                                                                <span className={`inline-flex font-medium rounded-full text-center px-2.5 py-0.5 ${claim?.actioned ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                                                                    {claim?.actioned ? 'Yes' : 'No'}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td className="p-2 text-center">
                                                            <Dropdown
                                                                overlay={
                                                                    <Menu>
                                                                        <Menu.Item onClick={() => openDetails(claim)}>View Details</Menu.Item>
                                                                        {
                                                                            claim?.admission &&
                                                                            <Link to={`/app/inpatient/admissions/${claim.admission}`}>
                                                                                <Menu.Item>Open Admission</Menu.Item>
                                                                            </Link>
                                                                        }
                                                                        {
                                                                            !claim?.actioned &&
                                                                            <Menu.Item onClick={() => markSelectedActioned(claim)}>Mark Actioned</Menu.Item>
                                                                        }
                                                                    </Menu>
                                                                }>
                                                                <a className="ant-dropdown-link flex items-center h-10" onClick={e => e.preventDefault()}>
                                                                    <span className="mr-2">Actions</span> <AiOutlineDown />
                                                                </a>
                                                            </Dropdown>
                                                        </td>
                                                    </tr>
                                                )
                                            }
                                        </tbody>
                                    </table>
                                    :
                                    <div className="w-full h-16 flex items-center justify-center">
                                        <p className="font-semibold text-base">No rejected claims were found for the selected filters</p>
                                    </div>
                            }
                        </Receive>
                        {Paginate}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default RejectedClaims
